import { useState } from "react";
import CountUp from "react-countup";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import {
  ClipboardList,
  Users,
  Settings2,
  Target,
  Factory,
  CheckCircle2,
  Truck,
  Flame,
  ShieldCheck,
  Package,
  Timer,
  Search,
  UserPlus,
  TrendingUp,
  TrendingDown,
} from "lucide-react";

// ─── Static Data ────────────────────────────────────────────────────────────

const monthlyOutput = [
  { month: "Jan", extrusion: 9800, foundry: 4200, packing: 7800 },
  { month: "Feb", extrusion: 11200, foundry: 5100, packing: 9400 },
  { month: "Mar", extrusion: 10500, foundry: 4800, packing: 8900 },
  { month: "Apr", extrusion: 13400, foundry: 6200, packing: 11200 },
  { month: "May", extrusion: 12450, foundry: 5700, packing: 10600 },
  { month: "Jun", extrusion: 14800, foundry: 6900, packing: 13100 },
];

const orderStatusData = [
  { name: "Completed", value: 48, color: "#10B981" },
  { name: "In Progress", value: 27, color: "#3F80F7" },
  { name: "Pending", value: 15, color: "#F59E0B" },
  { name: "On Hold", value: 10, color: "#EF4444" },
];

const weeklyProduction = [
  { day: "Mon", kg: 1820 },
  { day: "Tue", kg: 2140 },
  { day: "Wed", kg: 1960 },
  { day: "Thu", kg: 2380 },
  { day: "Fri", kg: 2250 },
  { day: "Sat", kg: 1700 },
];

const recentOrders = [
  { id: "SO-1082", customer: "Rajesh Industries", alloy: "AA6063", qty: "2,400 kg", status: "In Production", date: "03 Jun 2026" },
  { id: "SO-1081", customer: "Metro Profiles", alloy: "AA6082", qty: "1,850 kg", status: "QC Pending", date: "02 Jun 2026" },
  { id: "SO-1080", customer: "AluFab Ltd.", alloy: "AA6061", qty: "3,200 kg", status: "Packing", date: "01 Jun 2026" },
  { id: "SO-1079", customer: "SteelForm Co.", alloy: "AA6063", qty: "980 kg", status: "Completed", date: "31 May 2026" },
  { id: "SO-1078", customer: "Kiran Extruders", alloy: "AA6005", qty: "1,540 kg", status: "Aging", date: "30 May 2026" },
  { id: "SO-1077", customer: "Prime Metals", alloy: "AA6082", qty: "2,100 kg", status: "Completed", date: "29 May 2026" },
];

const productionPipeline = [
  { stage: "Sales Orders", count: 142, icon: ClipboardList, color: "#3F80F7", pct: 100 },
  { stage: "Foundry", count: 98, icon: Flame, color: "#F59E0B", pct: 69 },
  { stage: "In Production", count: 74, icon: Settings2, color: "#8B5CF6", pct: 52 },
  { stage: "QC", count: 52, icon: ShieldCheck, color: "#06B6D4", pct: 37 },
  { stage: "Aging", count: 38, icon: Timer, color: "#F97316", pct: 27 },
  { stage: "Packing", count: 21, icon: Package, color: "#10B981", pct: 15 },
];

const activityLog = [
  { msg: "Sales Order #1082 created for Rajesh Industries", time: "10:45 AM", type: "order" },
  { msg: "Die #D-2041 inwarded & allocated to Press #3", time: "10:12 AM", type: "die" },
  { msg: "Casting Batch #C-519 passed QC spectro test", time: "09:38 AM", type: "qc" },
  { msg: "Raw Material (AA6063 Billet) 2.5 MT received", time: "09:05 AM", type: "material" },
  { msg: "Furnace #2 heating cycle completed — 650°C", time: "08:30 AM", type: "furnace" },
  { msg: "Packing for SO-1075 dispatched to customer", time: "Yesterday", type: "dispatch" },
  { msg: "New Customer 'AluFab Ltd.' registered", time: "Yesterday", type: "customer" },
];

const topCustomers = [
  { name: "Rajesh Industries", orders: 24, kg: "18,240", pct: 88 },
  { name: "Metro Profiles", orders: 18, kg: "14,100", pct: 68 },
  { name: "AluFab Ltd.", orders: 15, kg: "11,800", pct: 57 },
  { name: "Prime Metals", orders: 12, kg: "9,400", pct: 45 },
  { name: "Kiran Extruders", orders: 9, kg: "7,200", pct: 35 },
];

const dieHealth = [
  { die: "D-2041", profile: "T-Section", press: 3, shots: 1842, status: "Good", pct: 78 },
  { die: "D-1987", profile: "L-Angle", press: 1, shots: 2941, status: "Fair", pct: 45 },
  { die: "D-2055", profile: "U-Channel", press: 2, shots: 820, status: "Excellent", pct: 96 },
  { die: "D-1901", profile: "Box Section", press: 3, shots: 3802, status: "Replace", pct: 12 },
];

// ─── Status Badge ─────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  Completed: "bg-green-50 text-green-700 border border-green-200",
  "In Production": "bg-blue-50 text-blue-700 border border-blue-200",
  "QC Pending": "bg-cyan-50 text-cyan-700 border border-cyan-200",
  Packing: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Aging: "bg-orange-50 text-orange-700 border border-orange-200",
  "On Hold": "bg-red-50 text-red-700 border border-red-200",
  Good: "bg-blue-50 text-blue-700 border border-blue-200",
  Fair: "bg-yellow-50 text-yellow-700 border border-yellow-200",
  Excellent: "bg-green-50 text-green-700 border border-green-200",
  Replace: "bg-red-50 text-red-700 border border-red-200",
};

const ACTIVITY_ICONS = {
  order: { bg: "bg-blue-100", iconColor: "text-blue-600", Icon: ClipboardList },
  die: { bg: "bg-purple-100", iconColor: "text-purple-600", Icon: Target },
  qc: { bg: "bg-cyan-100", iconColor: "text-cyan-600", Icon: ShieldCheck },
  material: { bg: "bg-yellow-100", iconColor: "text-yellow-700", Icon: Factory },
  furnace: { bg: "bg-orange-100", iconColor: "text-orange-600", Icon: Flame },
  dispatch: { bg: "bg-green-100", iconColor: "text-green-600", Icon: Truck },
  customer: { bg: "bg-pink-100", iconColor: "text-pink-600", Icon: UserPlus },
};

const ActivityIcon = ({ type }) => {
  const c = ACTIVITY_ICONS[type] || ACTIVITY_ICONS.order;
  const { Icon } = c;
  return (
    <div className={`w-9 h-9 ${c.bg} flex items-center justify-center flex-shrink-0`}>
      <Icon size={16} className={c.iconColor} strokeWidth={2.5} />
    </div>
  );
};

// ─── Custom Tooltips ──────────────────────────────────────────────────────────

/* Shared tooltip wrapper styles */
const tooltipWrap = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 0,
  borderTop: "3px solid #3F80F7",
  padding: "12px 16px",
  boxShadow: "0 8px 24px rgba(63,128,247,0.12)",
  minWidth: 160,
};

// AreaChart / BarChart tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={tooltipWrap}>
        {/* Label row */}
        <p
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "2px",
            color: "#3F80F7",
            textTransform: "uppercase",
            marginBottom: 8,
            borderBottom: "1px solid #f1f5f9",
            paddingBottom: 6,
          }}
        >
          {label}
        </p>
        {/* Series rows */}
        {payload.map((p, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: i === 0 ? 0 : 6,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                backgroundColor: p.color || "#3F80F7",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "1px",
                flex: 1,
              }}
            >
              {p.name}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 900,
                color: p.color || "#0f172a",
              }}
            >
              {p.value?.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// PieChart tooltip
const PieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const item = payload[0];
    return (
      <div style={tooltipWrap}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 10,
              height: 10,
              backgroundColor: item.payload.color,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "1.5px",
            }}
          >
            {item.name}
          </span>
        </div>
        <p
          style={{
            fontSize: 22,
            fontWeight: 900,
            color: item.payload.color,
            marginTop: 4,
            lineHeight: 1,
          }}
        >
          {item.value}%
        </p>
      </div>
    );
  }
  return null;
};


// ─── KPI Cards ────────────────────────────────────────────────────────────────

const kpiCards = [
  { label: "Active Sales Orders", value: 142, suffix: "", trend: "+12.5%", trendUp: true, sub: "vs last month", accent: "#3F80F7", Icon: ClipboardList },
  { label: "Total Customers", value: 87, suffix: "", trend: "+5", trendUp: true, sub: "new this month", accent: "#10B981", Icon: Users },
  { label: "Extrusion Output", value: 12450, suffix: " kg", trend: "+8.2%", trendUp: true, sub: "this month", accent: "#8B5CF6", Icon: Settings2 },
  { label: "Dies In Stock", value: 134, suffix: "", trend: "-6", trendUp: false, sub: "6 sent for repair", accent: "#F59E0B", Icon: Target },
  { label: "Raw Material (MT)", value: 48, suffix: ".6", trend: "+12 MT", trendUp: true, sub: "last inward", accent: "#06B6D4", Icon: Factory },
  { label: "QC Pass Rate", value: 94, suffix: "%", trend: "+1.4%", trendUp: true, sub: "this week", accent: "#10B981", Icon: CheckCircle2 },
  { label: "Pending Dispatch", value: 21, suffix: "", trend: "-3", trendUp: true, sub: "cleared today", accent: "#EF4444", Icon: Truck },
  { label: "Furnace Utilization", value: 78, suffix: "%", trend: "+5%", trendUp: true, sub: "avg efficiency", accent: "#F97316", Icon: Flame },
];

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("monthly");

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="flex flex-col min-h-full bg-gray-50 font-plus">

      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center px-6 py-4 bg-white border-b border-gray-200">
        <div>
          <h1 className="text-md font-semibold tracking-[3px] text-gray-900 uppercase">
            Extrusion Management Dashboard
          </h1>
        </div>
        <div className="flex items-center gap-3 mt-3 md:mt-0">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
            {today.toUpperCase()}
          </span>
          <div className="w-2 h-2 bg-green-500 animate-pulse" />
        </div>
      </div>

      {/* ── Scrollable Body ── */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">

        {/* ════ KPI Cards ════ */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {kpiCards.map((card, i) => (
            <div
              key={i}
              className="bg-white border border-gray-200 p-4 relative overflow-hidden hover:shadow-md transition-shadow duration-200"
            >
              <div className="absolute top-0 left-0 w-0.5 h-full" style={{ backgroundColor: card.accent }} />
              <div className="flex items-center justify-between mb-2">
                <div
                  className="w-8 h-8 flex items-center justify-center"
                  style={{ backgroundColor: card.accent + "18" }}
                >
                  <card.Icon size={15} style={{ color: card.accent }} strokeWidth={2.5} />
                </div>
                <span
                  className={`text-[11px] font-bold px-1.5 py-0.5 flex items-center gap-0.5 ${card.trendUp ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
                    }`}
                >
                  {card.trendUp ? <TrendingUp size={9} strokeWidth={3} /> : <TrendingDown size={9} strokeWidth={3} />}
                  {card.trend}
                </span>
              </div>
              <p className="text-[22px] font-black text-gray-900 leading-none">
                <CountUp end={card.value} duration={1.5} separator="," />{card.suffix}
              </p>
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mt-1 leading-tight">
                {card.label}
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5">{card.sub}</p>
            </div>
          ))}
        </div>

        {/* ════ Row 2: Pipeline + Pie ════ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Production Pipeline */}
          <div className="lg:col-span-2 bg-white border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-100">
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-[2px]">
                Production Pipeline Flow
              </h2>
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Active Batches
              </span>
            </div>
            <div className="space-y-4">
              {productionPipeline.map((stage, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <stage.icon size={15} style={{ color: stage.color }} strokeWidth={2.5} />
                      <span className="text-sm font-bold text-gray-700 uppercase tracking-wide">
                        {stage.stage}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-black" style={{ color: stage.color }}>
                        {stage.count}
                      </span>
                      <span className="text-xs text-gray-400 font-semibold">batches</span>
                    </div>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 overflow-hidden">
                    <div
                      className="h-full transition-all duration-700"
                      style={{ width: `${stage.pct}%`, backgroundColor: stage.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Status Pie */}
          <div className="bg-white border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-100">
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-[2px]">
                Order Status Split
              </h2>
              <span className="text-xs font-semibold text-gray-400 uppercase">100 Orders</span>
            </div>
            <div className="w-full h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {orderStatusData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />

                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 mt-2">
              {orderStatusData.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 flex-shrink-0" style={{ backgroundColor: item.color }} />
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      {item.name}
                    </span>
                  </div>
                  <span className="text-sm font-black" style={{ color: item.color }}>
                    {item.value}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ════ Row 3: Area Chart + Weekly Bar ════ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Monthly Area Chart */}
          <div className="lg:col-span-2 bg-white border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-100">
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-[2px]">
                Monthly Output Trend (kg)
              </h2>
              <div className="flex gap-4">
                {[
                  { key: "extrusion", label: "Extrusion", color: "#3F80F7" },
                  { key: "foundry", label: "Foundry", color: "#F59E0B" },
                  { key: "packing", label: "Packing", color: "#10B981" },
                ].map((l) => (
                  <div key={l.key} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5" style={{ backgroundColor: l.color }} />
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {l.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyOutput} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="extrusionGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3F80F7" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#3F80F7" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="foundryGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="packingGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={false} />

                  <Area type="monotone" dataKey="extrusion" stroke="#3F80F7" strokeWidth={2} fill="url(#extrusionGrad)" dot={false} name="Extrusion" />
                  <Area type="monotone" dataKey="foundry" stroke="#F59E0B" strokeWidth={2} fill="url(#foundryGrad)" dot={false} name="Foundry" />
                  <Area type="monotone" dataKey="packing" stroke="#10B981" strokeWidth={2} fill="url(#packingGrad)" dot={false} name="Packing" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Weekly Bar */}
          <div className="bg-white border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-100">
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-[2px]">
                Weekly Daily Output
              </h2>
              <span className="text-xs font-semibold text-gray-400 uppercase">This Week</span>
            </div>
            <div className="w-full h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weeklyProduction} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                  <XAxis dataKey="day" tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={false} />

                  <Bar dataKey="kg" name="KG Output">
                    {weeklyProduction.map((_, index) => (
                      <Cell key={index} fill={index === 3 ? "#3F80F7" : "#E8F0FE"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex items-center justify-between bg-gray-50 px-3 py-2">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Total This Week</span>
              <span className="text-base font-black text-primary">12,250 kg</span>
            </div>
          </div>
        </div>

        {/* ════ Row 4: Orders Table + Activity Log ════ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Recent Orders Table */}
          <div className="lg:col-span-2 bg-white border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-[2px]">
                Recent Sales Orders
              </h2>
              <button className="text-xs font-bold text-primary uppercase tracking-wider hover:underline">
                View All →
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    {["Order ID", "Customer", "Alloy", "Qty", "Status", "Date"].map((h) => (
                      <th key={h} className="px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((row, i) => (
                    <tr
                      key={i}
                      className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors duration-100"
                    >
                      <td className="px-4 py-3 text-sm font-black text-primary">{row.id}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-700">{row.customer}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-500 font-mono">{row.alloy}</td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-800">{row.qty}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] font-bold px-2 py-0.5 uppercase tracking-wide ${STATUS_COLORS[row.status] || "bg-gray-100 text-gray-600"}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs font-semibold text-gray-400">{row.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Activity Log */}
          <div className="bg-white border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-[2px]">
                Activity Log
              </h2>
              <div className="w-2.5 h-2.5 bg-green-500 animate-pulse" />
            </div>
            <div className="p-4 space-y-3 max-h-[320px] overflow-y-auto">
              {activityLog.map((item, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <ActivityIcon type={item.type} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-700 leading-snug">{item.msg}</p>
                    <p className="text-[11px] font-semibold text-gray-400 mt-0.5">{item.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ════ Row 5: Top Customers + Die Health ════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-8">

          {/* Top Customers */}
          <div className="bg-white border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-100">
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-[2px]">
                Top Customers
              </h2>
              <span className="text-xs font-semibold text-gray-400 uppercase">By Volume (kg)</span>
            </div>
            <div className="space-y-2">
              {topCustomers.map((cust, i) => {
                const colors = ["#3F80F7", "#8B5CF6", "#10B981", "#F59E0B", "#06B6D4"];
                const rankColors = ["#F59E0B", "#94a3b8", "#b45309", "#cbd5e1", "#e2e8f0"];
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 px-3 py-3 hover:bg-gray-50 transition-colors"
                  >
                    {/* Rank */}
                    <div
                      className="w-7 h-7 flex items-center justify-center text-xs font-black text-white flex-shrink-0"
                      style={{ backgroundColor: rankColors[i] }}
                    >
                      {i + 1}
                    </div>
                    {/* Name */}
                    <span className="text-sm font-semibold text-gray-700 flex-1 truncate">{cust.name}</span>
                    {/* Segmented blocks */}
                    <div className="flex gap-0.5 items-center">
                      {Array.from({ length: 8 }).map((_, di) => (
                        <div
                          key={di}
                          className="w-2 h-3.5 transition-all"
                          style={{
                            backgroundColor:
                              di < Math.round((cust.pct / 100) * 8) ? colors[i] : "#f1f5f9",
                          }}
                        />
                      ))}
                    </div>
                    {/* Volume */}
                    <span className="text-sm font-black text-gray-800 w-20 text-right">{cust.kg} kg</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Die Health Monitor */}
          <div className="bg-white border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-100">
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-[2px]">
                Die Health Monitor
              </h2>
              <span className="text-xs font-semibold text-gray-400 uppercase">4 Active Dies</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {dieHealth.map((die, i) => {
                const healthColor = die.pct >= 70 ? "#10B981" : die.pct >= 40 ? "#F59E0B" : "#EF4444";
                const healthBg = die.pct >= 70 ? "bg-green-50" : die.pct >= 40 ? "bg-yellow-50" : "bg-red-50";
                const r = 28;
                const circ = 2 * Math.PI * r;
                const arc = circ * 0.75;
                const offset = arc - (arc * die.pct) / 100;
                return (
                  <div
                    key={i}
                    className={`relative p-4 border border-gray-100 ${healthBg} overflow-hidden`}
                  >
                    {/* Status badge */}
                    <span className={`absolute top-2 right-2 text-[11px] font-bold px-2 py-0.5 uppercase tracking-wide ${STATUS_COLORS[die.status]}`}>
                      {die.status}
                    </span>

                    {/* SVG Gauge */}
                    <div className="flex justify-center mb-2">
                      <svg width="72" height="54" viewBox="0 0 72 54">
                        <circle cx="36" cy="42" r={r} fill="none" stroke="#e2e8f0" strokeWidth="5"
                          strokeDasharray={`${arc} ${circ}`} strokeDashoffset={0}
                          strokeLinecap="butt" transform="rotate(135 36 42)" />
                        <circle cx="36" cy="42" r={r} fill="none" stroke={healthColor} strokeWidth="5"
                          strokeDasharray={`${arc} ${circ}`} strokeDashoffset={offset}
                          strokeLinecap="butt" transform="rotate(135 36 42)"
                          style={{ transition: "stroke-dashoffset 0.8s ease" }} />
                        <text x="36" y="45" textAnchor="middle" fontSize="11" fontWeight="900"
                          fill={healthColor} fontFamily="Plus Jakarta Sans, sans-serif">
                          {die.pct}%
                        </text>
                      </svg>
                    </div>

                    {/* Info */}
                    <div className="text-center">
                      <p className="text-sm font-black text-gray-800">{die.profile}</p>
                      <p className="text-xs font-bold uppercase tracking-widest mt-0.5" style={{ color: healthColor }}>
                        {die.die}
                      </p>
                      <div className="mt-1.5 flex items-center justify-center gap-1">
                        <span className="text-[11px] font-semibold text-gray-400 uppercase">Press</span>
                        <span className="text-xs font-black text-gray-600">#{die.press}</span>
                        <span className="text-gray-300 mx-0.5">|</span>
                        <span className="text-[11px] font-semibold text-gray-400">{die.shots.toLocaleString()} shots</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
